import React from "react";
import { Helmet } from "react-helmet-async";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, Crown } from "lucide-react";

const LalBaghPalace = () => {
  const palaceImage = "/Indore/historical/lalBaghpalace0.jpg";
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Lal Bagh Palace",
    "description": "Magnificent European-style palace in Indore showcasing royal Holkar dynasty heritage and Indo-European architecture",
    "image": palaceImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Tukoganj",
      "addressLocality": "Indore",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Tu-Su 10:00-17:00",
    "priceRange": "₹15",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.5",
      "reviewCount": "1800"
    }
  };

  const highlights = [
    {
      icon: <Crown className="w-6 h-6" />,
      title: "European Architecture",
      description: "Stunning Indo-European architectural style with neoclassical elements and royal grandeur"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Crystal Chandeliers",
      description: "Magnificent crystal chandeliers imported from Europe adorning the royal halls"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Royal Collections",
      description: "Extensive collection of paintings, furniture, and artifacts from the Holkar dynasty"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Beautiful Gardens",
      description: "Well-maintained gardens surrounding the palace with landscaped lawns and fountains"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "₹15 for Indians, ₹200 for Foreigners" },
    { label: "Timings", value: "10:00 AM - 5:00 PM (Closed Mondays)" },
    { label: "Photography", value: "Interior photography charges apply" },
    { label: "Best Time", value: "October to March" },
    { label: "Duration", value: "2-3 hours" },
    { label: "Facilities", value: "Parking, Museum shop, Guided tours" }
  ];

  const palaceFeatures = [
    {
      title: "Darbar Hall",
      description: "Grand hall with Belgian stained glass windows and Italian marble floors",
      highlight: "Royal ceremonies venue"
    },
    {
      title: "Dining Hall", 
      description: "Elegant dining space with period furniture and European tableware",
      highlight: "Crystal chandelier centerpiece"
    },
    {
      title: "Library Room",
      description: "Personal library of Holkar rulers with rare books and manuscripts",
      highlight: "Historical documents collection"
    },
    {
      title: "Billiard Room",
      description: "Recreation room with original billiard table and gaming equipment",
      highlight: "Royal leisure activities"
    },
    {
      title: "Residential Quarters",
      description: "Private living spaces showcasing royal lifestyle and luxury",
      highlight: "Original royal furniture"
    },
    {
      title: "Art Gallery",
      description: "Collection of miniature paintings and royal portraits",
      highlight: "Holkar dynasty artwork"
    }
  ];

  const nearbyAttractions = [
    { name: "Rajwada Palace", distance: "3 km", time: "10 mins" },
    { name: "Central Museum", distance: "1.5 km", time: "5 mins" },
    { name: "Sarafa Bazaar", distance: "3.5 km", time: "12 mins" },
    { name: "Gandhi Hall", distance: "2 km", time: "8 mins" }
  ];

  return (
    <>
      <Helmet>
        <title>Lal Bagh Palace Indore - European Royal Architecture | Aaoghumen</title>
        <meta name="description" content="Visit Lal Bagh Palace Indore with Aaoghumen - explore magnificent European architecture, crystal chandeliers, royal collections, and Holkar dynasty heritage. Complete guide with timings, entry fees, and attractions." />
        <meta name="keywords" content="lal bagh palace indore, lal bagh palace timings, indore palace museum, holkar palace indore, european architecture indore, aaoghumen lal bagh, indore royal heritage, lal bagh palace entry fee, indore tourist attractions" />
        
        <meta property="og:title" content="Lal Bagh Palace Indore - Royal European Heritage | Aaoghumen" />
        <meta property="og:description" content="Discover the magnificent Lal Bagh Palace, showcasing European architecture and royal Holkar dynasty collections in the heart of Indore." />
        <meta property="og:image" content={palaceImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/lal-bagh-palace" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Lal Bagh Palace Indore - European Royal Heritage | Aaoghumen" />
        <meta name="twitter:description" content="Experience the grandeur of European architecture and royal collections at Lal Bagh Palace, Indore's crown jewel." />
        <meta name="twitter:image" content={palaceImage} />
        
        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/lal-bagh-palace" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-purple-600 via-pink-500 to-red-600 overflow-hidden">
          <img
            src={palaceImage}
            alt="Lal Bagh Palace Indore European architecture"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-yellow-300">Lal Bagh Palace</span> - Indore
              </h1>
              <p className="text-xl mb-6">
                Experience the grandeur of European architecture and royal elegance at this magnificent palace, showcasing the sophisticated taste and cultural legacy of the Holkar dynasty.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.5 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>10 AM - 5 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>₹15 Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Lal Bagh Palace</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Lal Bagh Palace stands as a testament to the cosmopolitan vision and refined taste of the Holkar rulers of Indore. Built in the early 20th century, this magnificent palace seamlessly blends European architectural styles with Indian sensibilities, creating a unique masterpiece that reflects the progressive outlook of the Holkar dynasty. The palace served as the residence of Maharaja Shivaji Rao Holkar and later became a symbol of Indore's royal heritage.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The palace's architecture draws inspiration from European castles and palaces, featuring neoclassical elements, intricate stonework, and grand halls that rival the finest European residences. The interiors are adorned with crystal chandeliers imported from Belgium, Italian marble, Persian carpets, and an extensive collection of European and Indian artwork. Each room tells a story of royal luxury and cultural sophistication.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, explore the palace's rich collections, understand the fascinating blend of Eastern and Western influences, and discover how the Holkar rulers embraced modernity while preserving their cultural roots. Our guided tours provide insights into the royal lifestyle, architectural innovations, and the historical significance of this remarkable palace in shaping Indore's cultural identity.
              </p>
            </div>
          </section>

          {/* Palace Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Palace Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-purple-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Palace Features */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Palace Rooms & Features</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {palaceFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <h3 className="text-xl font-bold text-purple-600 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">{feature.description}</p>
                  <div className="bg-yellow-100 p-2 rounded-lg">
                    <p className="text-yellow-800 font-medium text-xs">{feature.highlight}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-purple-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Photography & Experience Tips */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Palace Experience Tips</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-purple-600 mb-4">Architecture Photography</h3>
                <p className="text-gray-600 mb-4">Capture the European elegance and royal grandeur of palace interiors and exteriors.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Best lighting: Late morning</li>
                  <li>• Focus on chandeliers and details</li>
                  <li>• Garden views from palace</li>
                  <li>• Interior photography fees apply</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-purple-600 mb-4">Guided Tours</h3>
                <p className="text-gray-600 mb-4">Join expert-led tours to understand the palace's history, architecture, and royal collections.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• English and Hindi available</li>
                  <li>• Royal history insights</li>
                  <li>• Architectural details explained</li>
                  <li>• 1.5-hour comprehensive tour</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-purple-600 mb-4">Museum Exploration</h3>
                <p className="text-gray-600 mb-4">Explore the extensive collection of royal artifacts, paintings, and historical treasures.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• European artwork collection</li>
                  <li>• Royal furniture displays</li>
                  <li>• Historical manuscripts</li>
                  <li>• Holkar dynasty memorabilia</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Attractions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-purple-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Public Transport</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Bus:</strong> City buses to Tukoganj, well-connected route</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Auto:</strong> Available from all parts, ₹40-100 depending on distance</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Taxi:</strong> Uber/Ola easily available, comfortable option</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Private Vehicle</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Parking:</strong> Dedicated parking area available</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Location:</strong> Tukoganj area, well-marked signages</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Navigation:</strong> Search "Lal Bagh Palace Indore" on maps</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Plan Your Royal Experience</h2>
              <p className="text-xl mb-6">Discover European elegance and royal heritage with expert guides and historical insights</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 12345-67890</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>Tukoganj, Indore</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button className="bg-white text-purple-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors">
                  Book Palace Tour
                </button>
                <button className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-purple-600 transition-colors">
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default LalBaghPalace;